import { inject, Injectable } from '@angular/core';
import parsePhoneNumberFromString, { AsYouType, CountryCode, getCountries, getCountryCallingCode, isSupportedCountry, parsePhoneNumberWithError, PhoneNumber } from 'libphonenumber-js';
import { LoggingService } from './logging.service';

@Injectable({
  providedIn: 'root'
})
export class PhoneNumberService {
  loggingService = inject(LoggingService);

  defaultCountry = '1' as const;

  formatPartial(anyValue: string, countryCode: string | undefined): ExtendedPhoneNumber | undefined {
    try {
      const asYouType = new AsYouType({ defaultCallingCode: countryCode });
      const formatted = asYouType.input(anyValue);
      const phoneNumber = asYouType.getNumber();
      return { phoneNumber, partialFormatted: formatted };
    } catch {
      return undefined;
    }
  }

  format(anyValue: string, countryCode: string | null | undefined = this.defaultCountry) {
    return parsePhoneNumberWithError(anyValue, { defaultCallingCode: countryCode ?? undefined });
  }

  getRaw(phoneNumber: PhoneNumber | null | undefined) {
    if (!phoneNumber) return null;
    return phoneNumber.number;
  }

  formatFromE164String(value: string | null | undefined) {
    if (!value) return;
    return parsePhoneNumberFromString(value);
  }

  formatString(value: string, defaultCountry: CountryCode | null = null) {
    const format = this.format(value, defaultCountry);
    return `+${format.countryCallingCode} ${format.formatNational()}`;
  }

  tryGuessCountryCallingCode(): CountryCodeInfo | undefined {
    const language = navigator.language;
    const country = new Intl.Locale(language).region;
    if (!country) return;
    if (!isSupportedCountry(country)) return;
    const callingCode = getCountryCallingCode(country as CountryCode);
    return {
      callingCode,
      countryCode: country,
    };
  }

  isCountryCallingCodeValid(code: string) {
    return getCountries().some(iso => getCountryCallingCode(iso) === code);
  }
}

export interface ExtendedPhoneNumber {
  phoneNumber: PhoneNumber | null | undefined;
  partialFormatted?: string | null;
}

export interface CountryCodeInfo {
  callingCode?: string;
  countryCode?: string;
}